/**
 * Brand voice setup wizard — AI-09 §C1 (4 steps) + §C2 (train from past
 * emails) + minimal §C3 (re-open with prefill = edit). Saving PATCHes
 * data.voice through the existing brand-kit merge-patch path; nothing
 * persists until the final Save (the train flow only prefills).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../../lib/api";
import type { BrandKit, BrandKitVoice, VoiceTone } from "../../../lib/brandKits";
import { VOICE_TONES } from "../../../lib/brandKits";
import { saveVoice, type VoiceAnalysis } from "../../../lib/voiceAnalysis";
import { Button } from "../../ui/button";
import { TrainPanel } from "./TrainPanel";
import { ExamplesStep } from "./steps/ExamplesStep";
import { ToneStep } from "./steps/ToneStep";
import { VisualStep } from "./steps/VisualStep";
import { WordsStep } from "./steps/WordsStep";

const STEP_COUNT = 4;

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface BrandVoiceWizardProps {
  kit: BrandKit;
  onClose: () => void;
  onSaved: (kit: BrandKit) => void;
}

type View = "entry" | "train" | "steps";

export function BrandVoiceWizard({ kit, onClose, onSaved }: BrandVoiceWizardProps): JSX.Element {
  const { t } = useTranslation();
  const hasVoice = useMemo(
    () => kit.data.voice !== undefined && kit.data.voice !== null,
    [kit],
  );
  const [view, setView] = useState<View>("entry");
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<BrandKitVoice>(() => ({ ...(kit.data.voice ?? {}) }));
  const [trained, setTrained] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function update(patch: Partial<BrandKitVoice>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }

  function requestClose(): void {
    if (dirty && !confirmingClose) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, confirmingClose]);

  // Modal focus management (§K keyboard-navigable): focus moves into
  // the dialog on open, Tab wraps at the edges, and the trigger gets
  // focus back on close. Without the trap two Tabs walk out under the
  // overlay (caught by the staging tablet pass, 2026-06-12).
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const first = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dialogRef.current)?.focus();
    return () => opener?.focus();
  }, []);

  function trapTab(e: ReactKeyboardEvent<HTMLDivElement>): void {
    if (e.key !== "Tab") return;
    const root = dialogRef.current;
    if (root === null) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.hasAttribute("disabled"),
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (first === undefined || last === undefined) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function applyAnalysis(analysis: VoiceAnalysis): void {
    const s = analysis.suggestion;
    const tone = (VOICE_TONES as readonly string[]).includes(s.tone)
      ? (s.tone as VoiceTone)
      : null;
    update({
      tone,
      custom_tone: tone === null ? s.tone : null,
      personality_traits: s.personality_traits,
      language_level:
        (["conversational", "technical", "casual", "formal"] as const).find(
          (l) => l === s.language_level,
        ) ?? "conversational",
      signature_phrases: s.signature_phrases,
      banned_words: [...(draft.banned_words ?? []), ...s.banned_words_detected].filter(
        (w, i, all) => all.findIndex((v) => v.toLowerCase() === w.toLowerCase()) === i,
      ),
      tone_description: s.tone_description,
    });
    setTrained(true);
    setView("steps");
    setStep(0);
  }

  async function save(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    const now = new Date().toISOString();
    const voice: BrandKitVoice = {
      ...draft,
      source: trained ? "trained" : (draft.source ?? "manual"),
      trained_at: trained ? now : (draft.trained_at ?? null),
      updated_at: now,
    };
    try {
      const updated = await saveVoice(kit.id, voice);
      onSaved(updated);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Kit deleted while the wizard was open (AI-09 edge 9).
        setSaveError(t("brandVoice.errors.kit_gone"));
      } else if (err instanceof ApiError) {
        setSaveError(err.problem.detail || err.problem.title);
      } else {
        setSaveError(t("brandVoice.errors.save_failed"));
      }
    } finally {
      setSaving(false);
    }
  }

  const title = hasVoice ? t("brandVoice.title_edit") : t("brandVoice.title_create");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-4"
      role="presentation"
      onClick={requestClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trapTab}
      >
        <header className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
            {view === "steps" && (
              <p className="text-xs text-neutral-500">
                {t("brandVoice.progress", { current: step + 1, total: STEP_COUNT })} ·{" "}
                {t(`brandVoice.steps.${step}`)}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={requestClose}
            aria-label={t("brandVoice.close")}
          >
            ✕
          </Button>
        </header>

        {confirmingClose && (
          <div
            role="alertdialog"
            aria-label={t("brandVoice.discard.question")}
            className="flex items-center justify-between gap-3 border-b border-warning-500/40 bg-warning-50 px-4 py-2"
          >
            <p className="text-sm text-neutral-800">{t("brandVoice.discard.question")}</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="xs" onClick={() => setConfirmingClose(false)}>
                {t("brandVoice.discard.keep")}
              </Button>
              <Button type="button" variant="destructive" size="xs" onClick={onClose}>
                {t("brandVoice.discard.discard")}
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {view === "entry" && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">{t("brandVoice.entry.intro")}</p>
              <button
                type="button"
                onClick={() => setView("steps")}
                className="block w-full rounded-md border border-neutral-200 p-3 text-left hover:border-primary-400 hover:bg-primary-50"
              >
                <span className="block text-sm font-medium text-neutral-900">
                  {t("brandVoice.entry.manual_title")}
                </span>
                <span className="block text-xs text-neutral-500">
                  {t("brandVoice.entry.manual_description")}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setView("train")}
                className="block w-full rounded-md border border-neutral-200 p-3 text-left hover:border-primary-400 hover:bg-primary-50"
              >
                <span className="block text-sm font-medium text-neutral-900">
                  {t("brandVoice.entry.train_title")}
                </span>
                <span className="block text-xs text-neutral-500">
                  {t("brandVoice.entry.train_description")}
                </span>
              </button>
            </div>
          )}

          {view === "train" && (
            <TrainPanel kitId={kit.id} onUse={applyAnalysis} onBack={() => setView("entry")} />
          )}

          {view === "steps" && (
            <>
              {step === 0 && <ToneStep draft={draft} update={update} />}
              {step === 1 && <WordsStep draft={draft} update={update} />}
              {step === 2 && (
                <VisualStep draft={draft} update={update} colors={kit.data.colors} />
              )}
              {step === 3 && <ExamplesStep draft={draft} update={update} />}
            </>
          )}
        </div>

        {view === "steps" && (
          <footer className="flex items-center justify-between gap-3 border-t border-neutral-200 px-4 py-3">
            {saveError !== null ? (
              <p role="alert" className="truncate text-sm text-error-700">
                {saveError}
              </p>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => (step === 0 ? setView("entry") : setStep(step - 1))}
              >
                {t("brandVoice.back")}
              </Button>
              {step < STEP_COUNT - 1 ? (
                <Button type="button" variant="primary" size="sm" onClick={() => setStep(step + 1)}>
                  {t("brandVoice.next")}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={saving}
                  onClick={() => void save()}
                >
                  {t("brandVoice.save")}
                </Button>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
